// BrowserFileStore — OPFS-backed FileStore for the web/PWA build.
//
// OPFS (Origin Private File System) is the primary persistent storage
// mechanism for "this site has its own file tree" use cases. It's
// available on all modern browsers (including iOS/iPadOS Safari 17+),
// independent of the size limits that apply to localStorage / IndexedDB
// blobs, and survives across browser restarts as long as the user
// doesn't clear site data.
//
// What OPFS does NOT give us:
//   - Visibility from outside the page (the iPad Files app cannot see
//     these files; they're sandboxed to the origin). Acceptable for
//     v1 — we expose ZIP / PDF export buttons to get content out.
//   - A native "rename" primitive — `move()` exists in spec but isn't
//     in Safari yet. We implement rename as copy + delete.
//   - Synchronous APIs in the main thread (only async; sync access is
//     Worker-only via createSyncAccessHandle). Async is fine for our
//     usage frequency.

import type { FileEntry, FileStore } from './types';
import { splitPath } from './types';

export class BrowserFileStore implements FileStore {
  // Lazily resolved on first use, then cached. The handle itself is
  // tied to the document — survives reloads but not origin changes.
  private rootPromise: Promise<FileSystemDirectoryHandle> | null = null;

  private getRoot(): Promise<FileSystemDirectoryHandle> {
    if (!this.rootPromise) {
      if (!('storage' in navigator) || typeof navigator.storage.getDirectory !== 'function') {
        return Promise.reject(
          new Error(
            'OPFS unavailable: navigator.storage.getDirectory not present (need Safari 17+ / Chrome 102+ / Firefox 111+)',
          ),
        );
      }
      this.rootPromise = navigator.storage.getDirectory();
    }
    return this.rootPromise;
  }

  /**
   * Walk to the directory at `segments`. If `create` is true, missing
   * intermediate directories are created. Returns the directory handle.
   */
  private async resolveDir(
    segments: string[],
    create = false,
  ): Promise<FileSystemDirectoryHandle> {
    let dir = await this.getRoot();
    for (const seg of segments) {
      dir = await dir.getDirectoryHandle(seg, { create });
    }
    return dir;
  }

  /**
   * Resolve a file handle. `create=true` creates missing parents AND
   * the file itself; `create=false` throws on missing.
   */
  private async resolveFile(
    path: string,
    create = false,
  ): Promise<FileSystemFileHandle> {
    const segs = splitPath(path);
    if (segs.length === 0) throw new Error('path is empty (root is not a file)');
    const fileName = segs.pop()!;
    const parent = await this.resolveDir(segs, create);
    return parent.getFileHandle(fileName, { create });
  }

  async readTextFile(path: string): Promise<string> {
    const handle = await this.resolveFile(path, false);
    const file = await handle.getFile();
    return file.text();
  }

  async readBinaryFile(path: string): Promise<Uint8Array> {
    const handle = await this.resolveFile(path, false);
    const file = await handle.getFile();
    const buf = await file.arrayBuffer();
    return new Uint8Array(buf);
  }

  async writeTextFile(path: string, content: string): Promise<void> {
    const handle = await this.resolveFile(path, true);
    const writable = await handle.createWritable();
    try {
      await writable.write(content);
    } finally {
      await writable.close();
    }
  }

  async writeBinaryFile(path: string, content: Uint8Array): Promise<void> {
    const handle = await this.resolveFile(path, true);
    const writable = await handle.createWritable();
    try {
      // FileSystemWritableFileStream accepts BufferSource; Uint8Array satisfies that.
      // Cast through unknown because TS DOM lib types lag the spec a bit.
      await writable.write(content as unknown as BufferSource);
    } finally {
      await writable.close();
    }
  }

  async listDir(path: string): Promise<FileEntry[]> {
    const dir = await this.resolveDir(splitPath(path), false);
    const out: FileEntry[] = [];
    // FileSystemDirectoryHandle is async-iterable over [name, handle]. The
    // DOM lib types need a manual `as any` because the iterator type isn't
    // strictly typed on Safari's older TS definitions.
    const dirAny = dir as unknown as {
      entries(): AsyncIterable<[string, FileSystemHandle]>;
    };
    for await (const [name, handle] of dirAny.entries()) {
      if (handle.kind === 'file') {
        const file = await (handle as FileSystemFileHandle).getFile();
        out.push({
          name,
          kind: 'file',
          size: file.size,
          lastModified: file.lastModified,
        });
      } else {
        out.push({ name, kind: 'directory' });
      }
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }

  async exists(path: string): Promise<boolean> {
    const segs = splitPath(path);
    if (segs.length === 0) return true; // root always exists
    const last = segs.pop()!;
    try {
      const parent = await this.resolveDir(segs, false);
      try {
        await parent.getFileHandle(last);
        return true;
      } catch {
        try {
          await parent.getDirectoryHandle(last);
          return true;
        } catch {
          return false;
        }
      }
    } catch {
      return false;
    }
  }

  async ensureDir(path: string): Promise<void> {
    await this.resolveDir(splitPath(path), true);
  }

  async deleteFile(path: string): Promise<void> {
    const segs = splitPath(path);
    if (segs.length === 0) throw new Error('cannot delete root');
    const fileName = segs.pop()!;
    const parent = await this.resolveDir(segs, false);
    // removeEntry throws if missing — that's the desired behaviour.
    // We could also pre-check kind, but removeEntry on a directory with
    // recursive=false throws InvalidModificationError, which is acceptable.
    await parent.removeEntry(fileName, { recursive: false });
  }

  async deleteDir(path: string): Promise<void> {
    const segs = splitPath(path);
    if (segs.length === 0) throw new Error('cannot delete root');
    const dirName = segs.pop()!;
    const parent = await this.resolveDir(segs, false);
    await parent.removeEntry(dirName, { recursive: true });
  }

  async rename(oldPath: string, newPath: string): Promise<void> {
    if (await this.exists(newPath)) {
      throw new Error(`rename target already exists: '${newPath}'`);
    }
    if (!(await this.exists(oldPath))) {
      throw new Error(`rename source missing: '${oldPath}'`);
    }
    // OPFS as of 2026-04 has no native rename in Safari. Fall back to
    // recursive copy + delete. For deeply nested project trees this is
    // O(total bytes) but happens only when user explicitly renames a
    // project, not in hot paths.
    await this.copyRecursive(oldPath, newPath);
    // If oldPath is a file, deleteFile; if directory, deleteDir. Probe.
    try {
      await this.deleteFile(oldPath);
    } catch {
      await this.deleteDir(oldPath);
    }
  }

  /**
   * Copy a single file or a whole directory tree. Used internally by
   * `rename`; not part of the FileStore interface (yet) since use-cases
   * outside rename are rare.
   */
  private async copyRecursive(from: string, to: string): Promise<void> {
    const fromSegs = splitPath(from);
    if (fromSegs.length === 0) throw new Error('cannot copy root');
    const fromName = fromSegs.pop()!;
    const fromParent = await this.resolveDir(fromSegs, false);

    // File case
    try {
      const fileHandle = await fromParent.getFileHandle(fromName);
      const file = await fileHandle.getFile();
      const bytes = new Uint8Array(await file.arrayBuffer());
      await this.writeBinaryFile(to, bytes);
      return;
    } catch {
      // not a file, fall through to directory case
    }

    // Directory case
    const dirHandle = await fromParent.getDirectoryHandle(fromName);
    await this.ensureDir(to);
    const dirAny = dirHandle as unknown as {
      entries(): AsyncIterable<[string, FileSystemHandle]>;
    };
    for await (const [childName] of dirAny.entries()) {
      await this.copyRecursive(`${from}/${childName}`, `${to}/${childName}`);
    }
  }
}

/**
 * Default singleton — most app code can just import `defaultFileStore`
 * and call methods on it. Tests can construct fresh instances if they
 * want isolated state.
 */
export const defaultFileStore: FileStore = new BrowserFileStore();
